import { makeAutoObservable, runInAction } from 'mobx';
import { Common } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { GoogleTagManagerState } from 'src/domains/layouts/state/googleState/GoogleTagManagerState';
import { AccountState } from 'src/domains/players/state/accountState/AccountState';
import { BasicDataModel } from 'src/domains/players/state/BasicDataModel';
import { TrpcClient } from 'src/appState/TrpcClient';
import { MultiDepositSteps } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/multiDepositJourney/MultiDepositSteps.state';
import { DepositErrorMessagesState } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/errorMessages/DepositErrorMessages.state';
import { GooglePayProviderZod, WorldpayCardProviderZod } from 'src/domains/common/configFeatures/ConfigFeatures';
import { getSavedCards } from 'src_server/trpc/types/payments';
import { WorldpaySessionState } from './WorldpayFrameOptions';
import { loadScript } from './loadScript';
import { z } from 'zod';
import { DeviceDataSchema, SuccessResponseSchema, ThreeDsChallengeSchema } from 'src_server/trpc/types/authenticate3ds';

type WorldpayCardProviderType = z.infer<typeof WorldpayCardProviderZod>;
type WorldpayAuthenticationType = z.infer<typeof SuccessResponseSchema>['authentication'];
type WorldpayAuthenticationRequestType = {
    threeDS: NonNullable<WorldpayAuthenticationType>;
};

type WorldpayCreatePaymentMethod =
    | {
          type: 'Card';
          flow: 'new';
          cardSession: string;
          cardCvvSession: string;
          cardHolderName: string;
      }
    | {
          type: 'Card';
          flow: 'saved';
          paymentMethodId?: string;
          tokenHref: string;
          savedCardCvcSession: string;
      };
type GooglePayProviderType = z.infer<typeof GooglePayProviderZod>;

export type WorldpayPaymentMethodType = 'debit-card' | 'apple-pay' | 'google-pay';

export interface ChallengeData {
    url: string;
    jwt: string;
    reference: string;
    backendOrigin: string;
}

interface ApplePaySession {
    completeMerchantValidation(merchantSession: unknown): void;
    completePaymentMethodSelection(update: {
        newTotal: { label: string; amount: string; type: 'final' };
        newLineItems?: Array<{ label: string; amount: string }>;
    }): void;
    completePayment(status: number): void;
    onvalidatemerchant: ((event: { validationURL: string }) => void) | null;
    onpaymentmethodselected: ((event: { paymentMethod: ApplePayPaymentMethod }) => void) | null;
    onpaymentauthorized: ((event: { payment: { token: ApplePayPaymentToken } }) => void) | null;
    oncancel: (() => void) | null;
    begin(): void;
}

interface ApplePayPaymentMethod {
    displayName?: string;
    network?: string;
    type?: string;
}

interface ApplePayPaymentToken {
    paymentMethod: ApplePayPaymentMethod;
    transactionIdentifier: string;
    paymentData: Record<string, unknown>;
}

interface GooglePaymentsClient {
    isReadyToPay(request: unknown): Promise<{ result: boolean }>;
    loadPaymentData(request: unknown): Promise<{
        paymentMethodData: {
            tokenizationData: {
                token: string;
            };
        };
    }>;
    createButton(request: {
        onClick(): void;
        allowedPaymentMethods: unknown[];
        buttonSizeMode?: 'static' | 'fill';
    }): HTMLButtonElement;
}

interface GooglePaymentOptions {
    environment: 'TEST' | 'PRODUCTION';
    merchantInfo?: {
        merchantName: string;
        merchantId?: string;
    };
}

interface ApplePaySessionConstructor {
    new (version: number, paymentRequest: unknown): ApplePaySession;
    canMakePayments(): boolean;
    STATUS_SUCCESS: number;
    STATUS_FAILURE: number;
}

const APPLE_PAY_SDK_URL = 'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js';

declare global {
    interface Window {
        ApplePaySession?: ApplePaySessionConstructor;
        google?: {
            payments: {
                api: {
                    PaymentsClient: new (config: GooglePaymentOptions) => GooglePaymentsClient;
                };
            };
        };
    }
}

export class WorldpayTabState {
    public isSdkReady: boolean = false;
    public isSubmitting: boolean = false;
    public cardHolderName: string = '';
    public isApplePayAvailable: boolean | null = null;
    public savedPaymentMethods: getSavedCards.SavedCardInterfaceType[] | null = null;
    public selectedSavedPaymentMethodId: string = 'none';
    public challengeData: ChallengeData | null = null;
    public readonly worldpayCardConfig: WorldpayCardProviderType | null;
    public readonly googlePayConfig: GooglePayProviderType | null;

    private readonly account: AccountState;
    private readonly googleTagManager: GoogleTagManagerState;
    private readonly basicDataModel: BasicDataModel;
    private readonly configComponents: ConfigComponents;
    private readonly trpcClient: TrpcClient;
    private addedDepositTag: boolean = false;
    private challengeResolve: ((challengeReference: string) => void) | null = null;
    private challengeReject: ((error: Error) => void) | null = null;

    public constructor(
        common: Common,
        private readonly amount: string,
        public readonly paymentMethod: WorldpayPaymentMethodType,
        public readonly stepsState: MultiDepositSteps,
        private readonly isSignup: boolean,
        private readonly errorMessagesState: DepositErrorMessagesState
    ) {
        this.account = AccountState.get(common);
        this.googleTagManager = GoogleTagManagerState.get(common);
        this.basicDataModel = BasicDataModel.get(common);
        this.configComponents = ConfigComponents.get(common);
        this.trpcClient = common.trpcClient;

        const worldpayConfigResult = WorldpayCardProviderZod.safeParse(
            this.configComponents.config.paymentProviders.debitCard
        );
        if (worldpayConfigResult.success) {
            this.worldpayCardConfig = worldpayConfigResult.data;
        } else {
            console.error('WorldpayTabState - invalid worldpay config', worldpayConfigResult.error);
            this.worldpayCardConfig = null;
            this.stepsState.redirectToFailureView('serverIssue');
        }
        this.googlePayConfig = this.configComponents.config.paymentProviders.googlePay ?? null;

        makeAutoObservable(this);
    }

    public get currency(): string {
        return this.basicDataModel.currency ?? 'GBP';
    }

    public get depositAmount(): string {
        return this.amount;
    }

    public get operatorFullName(): string {
        return this.configComponents.config.operatorFullName;
    }

    public handleSdkReady = (): void => {
        this.isSdkReady = true;
    };

    public handleSdkError = (): void => {
        this.stepsState.redirectToFailureView('serverIssue');
    };

    public handleCardHolderNameChange = (value: string): void => {
        this.cardHolderName = value;
    };

    public handleSubmitting = (): void => {
        this.isSubmitting = true;
    };

    private isCheckoutInvalidFormError = (error: unknown): boolean => {
        return typeof error === 'string' && error.startsWith('invalidForm:');
    };

    public handleCardSessionError = (error?: unknown): void => {
        if (this.isCheckoutInvalidFormError(error)) {
            this.isSubmitting = false;
            return;
        }

        this.isSubmitting = false;
        this.errorMessagesState.createErrorMessage('unknown');
        this.stepsState.redirectToFailureView('serverIssue');
    };

    private isGooglePayCancelError = (error: unknown): boolean => {
        if (typeof error !== 'object' || error === null) {
            return false;
        }

        const candidate = error as { name?: unknown; statusCode?: unknown };
        return candidate.name === 'AbortError' || candidate.statusCode === 'CANCELED';
    };

    private handleGooglePayError = (error: unknown): void => {
        if (this.isGooglePayCancelError(error)) {
            this.isSubmitting = false;
            this.stepsState.redirectToFailureView('cancelledTransaction');
            return;
        }

        console.error('worldpay-google-pay-error', error);
        this.isSubmitting = false;
        this.errorMessagesState.createErrorMessage('unknown');
        this.stepsState.redirectToFailureView('serverIssue');
    };

    private handleApplePayError = (error: unknown): void => {
        console.error('worldpay-apple-pay-error', error);
        this.isSubmitting = false;
        this.errorMessagesState.createErrorMessage('unknown');
        this.stepsState.redirectToFailureView('serverIssue');
    };

    public loadSavedPaymentMethods = async (): Promise<void> => {
        if (this.paymentMethod !== 'debit-card') {
            return;
        }

        const savedMethodsProvider = this.configComponents.config.paymentProviders.debitCard.type;
        if (savedMethodsProvider === null) {
            runInAction(() => {
                this.savedPaymentMethods = null;
            });
            return;
        }

        try {
            const response = await this.trpcClient.client.payments.getSavedCard.query({
                providers: [savedMethodsProvider],
            });

            if (response.responseStatus === 'error') {
                runInAction(() => {
                    this.savedPaymentMethods = null;
                });
                return;
            }

            runInAction(() => {
                if (response.responseStatus === 'success') {
                    this.savedPaymentMethods = response.response;
                } else {
                    this.savedPaymentMethods = [];
                }
            });
        } catch (error) {
            console.error('worldpay-load-saved-payment-methods-error', error);
            runInAction(() => {
                this.savedPaymentMethods = [];
            });
        }
    };

    public handleSavedPaymentMethodChange = (value: string): void => {
        this.selectedSavedPaymentMethodId = value;
    };

    public get hasSavedPaymentMethods(): boolean {
        return this.depositSavedPaymentMethods !== null && this.depositSavedPaymentMethods.length > 0;
    }

    public get selectedSavedPaymentMethod(): getSavedCards.SavedCardInterfaceType | undefined {
        if (this.depositSavedPaymentMethods === null) {
            return undefined;
        }

        return this.depositSavedPaymentMethods.find((method) => method.id === this.selectedSavedPaymentMethodId);
    }

    public get depositSavedPaymentMethods(): getSavedCards.SavedCardInterfaceType[] | null {
        if (this.savedPaymentMethods === null) {
            return null;
        }

        return this.savedPaymentMethods.filter((method) => {
            const subtype = method.subtype ?? null;
            if (subtype === 'apple-pay' || subtype === 'google-pay') {
                return false;
            }

            return !!method.number;
        });
    }

    public submitSavedPayment = async (savedCardCvcSession: string): Promise<void> => {
        const account = this.account.account;
        if (account === null) {
            console.error('WorldpayTabState - submitSavedPayment - user is anonymous');
            this.stepsState.redirectToSetAmount();
            return;
        }

        const selectedSavedPaymentMethod = this.selectedSavedPaymentMethod;
        const providerReference = selectedSavedPaymentMethod?.providerReference ?? null;
        if (selectedSavedPaymentMethod === undefined || providerReference === null || providerReference === '') {
            console.error('WorldpayTabState - submitSavedPayment - missing saved payment method reference');
            this.handleCardSessionError();
            return;
        }

        const currency = this.basicDataModel.currency ?? 'GBP';
        const amount = parseFloat(this.amount);
        const paymentMethod: WorldpayCreatePaymentMethod = {
            type: 'Card',
            flow: 'saved',
            paymentMethodId: selectedSavedPaymentMethod.id,
            tokenHref: providerReference,
            savedCardCvcSession,
        };

        await this.preparePayment({
            amount,
            currency,
            paymentMethod,
            tokenHref: providerReference,
        });
    };

    private preparePayment = async ({
        amount,
        currency,
        paymentMethod,
        tokenHref,
        cardHolderName,
    }: {
        amount: number;
        currency: string;
        paymentMethod: WorldpayCreatePaymentMethod;
        tokenHref: string;
        cardHolderName?: string;
    }): Promise<void> => {
        try {
            const response = await this.trpcClient.client.worldpayRouter.preparePayment.mutate({
                amount,
                currencyCode: currency,
                description: 'Top-up',
            });

            if (response.responseStatus === 'error') {
                const error = response.data.errors[0];
                this.errorMessagesState.createErrorMessage(error?.code ?? undefined, error?.message ?? undefined);
                runInAction(() => {
                    this.isSubmitting = false;
                });
                this.stepsState.redirectToFailureView('failWithReceipt', {
                    transactionId: undefined,
                    data: {
                        currency,
                        amount,
                        createdAt: new Date().toISOString(),
                        status: 'failed',
                    },
                });
                return;
            }

            await this.runDeviceDataCollection(
                response.response.transactionId,
                amount,
                currency,
                tokenHref,
                paymentMethod,
                cardHolderName
            );
        } catch (error) {
            console.error('worldpay-prepare-payment-error', error);
            runInAction(() => {
                this.isSubmitting = false;
            });
            this.stepsState.redirectToFailureView('serverIssue');
        }
    };

    private submitPayment = async ({
        amount,
        currency,
        paymentMethod,
        tokenHref,
        allowThreeDs,
        transactionId,
        threeDsAuthentication,
    }: {
        amount: number;
        currency: string;
        paymentMethod: WorldpayCreatePaymentMethod;
        tokenHref: string;
        allowThreeDs: boolean;
        transactionId?: number;
        threeDsAuthentication?: WorldpayAuthenticationRequestType;
    }): Promise<void> => {
        try {
            const response = await this.trpcClient.client.worldpayRouter.createPayment.mutate({
                amount,
                currencyCode: currency,
                paymentMethod,
                ...(threeDsAuthentication ? { authentication: threeDsAuthentication } : {}),
                ...(transactionId !== undefined ? { transactionId } : {}),
                description: 'Top-up',
            });

            if (response.responseStatus === 'error') {
                const error = response.data.errors[0];
                this.errorMessagesState.createErrorMessage(error?.code ?? undefined, error?.message ?? undefined);
                runInAction(() => {
                    this.isSubmitting = false;
                });
                this.stepsState.redirectToFailureView('failWithReceipt', {
                    transactionId: undefined,
                    data: {
                        currency,
                        amount,
                        createdAt: new Date().toISOString(),
                        status: 'failed',
                    },
                });
                return;
            }

            const responseStatus = response.response.status.toLowerCase();
            if (responseStatus === 'initiated' || responseStatus === 'pending') {
                if (allowThreeDs === false) {
                    console.error('worldpay-payment-three-ds-unexpected');
                    this.stepsState.redirectToFailureView('serverIssue');
                    return;
                }

                await this.runDeviceDataCollection(
                    response.response.transactionId,
                    amount,
                    currency,
                    tokenHref,
                    paymentMethod,
                    this.cardHolderName
                );
                return;
            }

            this.handlePaymentSuccess(amount, response.response.transactionId);
        } catch (error) {
            console.error('worldpay-create-payment-error', error);
            runInAction(() => {
                this.isSubmitting = false;
            });
            this.stepsState.redirectToFailureView('serverIssue');
        }
    };

    public handleApplePayAvailability = (isAvailable: boolean): void => {
        this.isApplePayAvailable = isAvailable;
        if (isAvailable === false) {
            this.errorMessagesState.clearErrorMessage();
            this.errorMessagesState.errors.push({
                type: 'unknown',
                message:
                    'Apple Pay is not available on this device. Please add a card to Apple Wallet or choose another payment method.',
            });
        }
    };

    public handleGooglePayAvailability = (isAvailable: boolean): void => {
        if (isAvailable === false) {
            this.errorMessagesState.clearErrorMessage();
            this.errorMessagesState.errors.push({
                type: 'unknown',
                message:
                    'Google Pay is not available for this device or card setup. Please choose another payment method.',
            });
        }
    };

    public handleMerchantValidation = async (validationURL: string): Promise<unknown> => {
        const result = await this.trpcClient.client.worldpayRouter.validateApplePayMerchant.mutate({ validationURL });
        if (result.responseStatus === 'error') {
            throw new Error(result.data.errors[0]?.message ?? 'Merchant validation failed');
        }
        return result.response.merchantSession;
    };

    public startApplePayPayment = async (): Promise<void> => {
        try {
            await loadScript(APPLE_PAY_SDK_URL);

            const ApplePaySession = window.ApplePaySession;
            if (ApplePaySession === undefined) {
                this.handleApplePayAvailability(false);
                return;
            }
            const isApplePayAvailable = window.isSecureContext === true && ApplePaySession?.canMakePayments() === true;
            this.handleApplePayAvailability(isApplePayAvailable);

            if (isApplePayAvailable === false) {
                return;
            }

            this.handleSdkReady();

            const session = this.createApplePaySession(ApplePaySession);
            session.begin();
        } catch (e) {
            console.error('worldpay-apple-pay-sdk-init', e);
            this.handleApplePayAvailability(false);
        }
    };

    public handleApplePayPayment = async (walletToken: string, cardHolderName?: string): Promise<void> => {
        const account = this.account.account;
        if (account === null) {
            console.error('WorldpayTabState - handleApplePayPayment - user is anonymous');
            this.stepsState.redirectToSetAmount();
            return;
        }

        const currency = this.basicDataModel.currency ?? 'GBP';

        try {
            const amount = parseFloat(this.amount);

            const response = await this.trpcClient.client.worldpayRouter.createPayment.mutate({
                amount,
                currencyCode: currency,
                paymentMethod: {
                    type: 'ApplePay',
                    walletToken,
                    cardHolderName,
                },
                description: 'Top-up',
            });

            if (response.responseStatus === 'error') {
                const error = response.data.errors[0];
                this.errorMessagesState.createErrorMessage(error?.code ?? undefined, error?.message ?? undefined);
                runInAction(() => {
                    this.isSubmitting = false;
                });
                this.stepsState.redirectToFailureView('failWithReceipt', {
                    transactionId: undefined,
                    data: {
                        currency,
                        amount,
                        createdAt: new Date().toISOString(),
                        status: 'failed',
                    },
                });
                return;
            }

            if (!this.addedDepositTag) {
                runInAction(() => {
                    this.addedDepositTag = true;
                });
                this.googleTagManager.addDepositTag(this.account.account);
            }

            if (this.isSignup) {
                this.googleTagManager.depositedMoney(parseFloat(this.amount), true);
                this.googleTagManager.gtmSignUpStepFour(parseFloat(this.amount));
            } else {
                this.googleTagManager.depositedMoney(parseFloat(this.amount), false);
            }

            this.stepsState.redirectToFinalView(response.response.transactionId ?? undefined, 'worldpay');
        } catch (e) {
            console.error('worldpay-apple-pay-error', e);
            runInAction(() => {
                this.isSubmitting = false;
            });
            this.stepsState.redirectToFailureView('serverIssue');
        }
    };

    public startGooglePayPayment = async (): Promise<void> => {
        try {
            const PaymentsClient = window.google?.payments.api?.PaymentsClient;
            if (PaymentsClient === undefined) {
                throw new Error('Google Pay SDK failed to load');
            }

            if (this.googlePayConfig === null) {
                throw new Error('Google Pay configuration is missing');
            }

            const allowedPaymentMethods = [
                {
                    type: 'CARD',
                    parameters: {
                        allowedAuthMethods: ['CRYPTOGRAM_3DS'],
                        allowedCardNetworks: ['MASTERCARD', 'VISA'],
                    },
                    tokenizationSpecification: {
                        type: 'PAYMENT_GATEWAY',
                        parameters: {
                            gateway: 'worldpay',
                            gatewayMerchantId: this.googlePayConfig.gatewayMerchantId,
                        },
                    },
                },
            ];

            const merchantInfo = {
                merchantName: this.operatorFullName,
                merchantId: this.googlePayConfig.googleMerchantId,
            };

            const paymentsClient = new PaymentsClient({
                environment: this.googlePayConfig.environment,
                merchantInfo,
            });

            const { result } = await paymentsClient.isReadyToPay({
                apiVersion: 2,
                apiVersionMinor: 0,
                existingPaymentMethodRequired: true,
                allowedPaymentMethods,
            });

            if (!result) {
                this.handleGooglePayAvailability(false);
                return;
            }

            const paymentData = await paymentsClient.loadPaymentData({
                apiVersion: 2,
                apiVersionMinor: 0,
                allowedPaymentMethods,
                transactionInfo: {
                    totalPriceStatus: 'FINAL',
                    totalPrice: this.depositAmount,
                    currencyCode: this.currency,
                    countryCode: 'GB',
                },
                merchantInfo,
            });

            const walletToken = paymentData.paymentMethodData.tokenizationData.token;
            await this.handleGooglePayPayment(walletToken);
        } catch (e) {
            this.handleGooglePayError(e);
        }
    };

    public handleGooglePayPayment = async (walletToken: string): Promise<void> => {
        const account = this.account.account;
        if (account === null) {
            console.error('WorldpayTabState - handleGooglePayPayment - user is anonymous');
            this.stepsState.redirectToSetAmount();
            return;
        }

        const currency = this.basicDataModel.currency ?? 'GBP';

        try {
            const amount = parseFloat(this.amount);

            const response = await this.trpcClient.client.worldpayRouter.createPayment.mutate({
                amount,
                currencyCode: currency,
                paymentMethod: {
                    type: 'GooglePay',
                    walletToken,
                },
                description: 'Top-up',
            });

            if (response.responseStatus === 'error') {
                const error = response.data.errors[0];
                this.errorMessagesState.createErrorMessage(error?.code ?? undefined, error?.message ?? undefined);
                runInAction(() => {
                    this.isSubmitting = false;
                });
                this.stepsState.redirectToFailureView('failWithReceipt', {
                    transactionId: undefined,
                    data: {
                        currency,
                        amount,
                        createdAt: new Date().toISOString(),
                        status: 'failed',
                    },
                });
                return;
            }

            if (!this.addedDepositTag) {
                runInAction(() => {
                    this.addedDepositTag = true;
                });
                this.googleTagManager.addDepositTag(this.account.account);
            }

            if (this.isSignup) {
                this.googleTagManager.depositedMoney(parseFloat(this.amount), true);
                this.googleTagManager.gtmSignUpStepFour(parseFloat(this.amount));
            } else {
                this.googleTagManager.depositedMoney(parseFloat(this.amount), false);
            }

            this.stepsState.redirectToFinalView(response.response.transactionId ?? undefined, 'worldpay');
        } catch (e) {
            this.handleGooglePayError(e);
        }
    };

    private createApplePaySession = (ApplePaySession: ApplePaySessionConstructor): ApplePaySession => {
        const paymentRequest = {
            countryCode: 'GB',
            currencyCode: this.currency,
            supportedNetworks: ['visa', 'masterCard', 'maestro'],
            merchantCapabilities: ['supports3DS'],
            requiredBillingContactFields: ['postalAddress', 'name'],
            total: {
                label: this.operatorFullName,
                amount: this.depositAmount,
                type: 'final' as const,
            },
        };
        const session = new ApplePaySession(3, paymentRequest);

        session.onvalidatemerchant = async (event): Promise<void> => {
            try {
                const merchantSession = await this.handleMerchantValidation(event.validationURL);
                session.completeMerchantValidation(merchantSession);
            } catch (e) {
                session.completePayment(ApplePaySession.STATUS_FAILURE);
                this.handleApplePayError(e);
            }
        };

        session.onpaymentmethodselected = (): void => {
            session.completePaymentMethodSelection({
                newTotal: {
                    label: this.operatorFullName,
                    amount: this.depositAmount,
                    type: 'final',
                },
                newLineItems: [],
            });
        };

        session.onpaymentauthorized = async (event): Promise<void> => {
            const walletToken = JSON.stringify(event.payment.token.paymentData);
            const cardHolderName = event.payment.token.paymentMethod.displayName?.trim();
            try {
                this.handleSubmitting();
                await this.handleApplePayPayment(walletToken, cardHolderName);
                session.completePayment(ApplePaySession.STATUS_SUCCESS);
            } catch (e) {
                this.handleApplePayError(e);
                session.completePayment(ApplePaySession.STATUS_FAILURE);
            }
        };

        session.oncancel = (): void => {
            this.isSubmitting = false;
            this.stepsState.redirectToFailureView('cancelledTransaction');
        };

        return session;
    };

    public handleSessionState = async (session: WorldpaySessionState): Promise<void> => {
        const account = this.account.account;
        if (account === null) {
            console.error('WorldpayTabState - handleSessionState - user is anonymous');
            this.isSubmitting = false;
            this.stepsState.redirectToSetAmount();
            return;
        }

        const currency = this.basicDataModel.currency ?? 'GBP';
        const amount = parseFloat(this.amount);
        const paymentMethod: WorldpayCreatePaymentMethod = {
            type: 'Card',
            flow: 'new',
            cardSession: session.card,
            cardCvvSession: session.cvv,
            cardHolderName: this.cardHolderName,
        };

        await this.preparePayment({
            amount,
            currency,
            paymentMethod,
            tokenHref: session.card,
            cardHolderName: this.cardHolderName,
        });
    };

    private runDeviceDataCollection = async (
        transactionId: number,
        amount: number,
        currency: string,
        tokenHref: string,
        paymentMethod: WorldpayCreatePaymentMethod,
        cardHolderName?: string
    ): Promise<void> => {
        try {
            const ddcResponse = await this.trpcClient.client.worldpayRouter.initializeDeviceData.mutate({
                transactionId,
            });

            if (ddcResponse.responseStatus === 'error') {
                console.error('worldpay-ddc-error', ddcResponse.data);
                this.stepsState.redirectToFailureView('serverIssue');
                return;
            }

            const { jwt, url, bin, cardinalOrigin, backendOrigin } = ddcResponse.response;
            const collectionReference = await this.postDdcIframe(url, jwt, cardinalOrigin, bin ?? undefined);

            const deviceData = {
                acceptHeader: '*/*',
                userAgentHeader: navigator.userAgent,
                ...(collectionReference !== undefined ? { collectionReference } : {}),
                browserLanguage: navigator.language,
                browserJavaEnabled: false,
                browserColorDepth: String(screen.colorDepth),
                browserScreenHeight: screen.height,
                browserScreenWidth: screen.width,
                timeZone: String(new Date().getTimezoneOffset()),
                browserJavascriptEnabled: true,
            };

            const challenge = {
                returnUrl: backendOrigin,
                windowSize: 'fullPage',
            };

            await this.run3dsAuth(
                transactionId,
                amount,
                currency,
                tokenHref,
                paymentMethod,
                deviceData,
                challenge,
                backendOrigin,
                cardHolderName
            );
        } catch (error) {
            console.error('worldpay-3ds-flow-error', error);
            this.stepsState.redirectToFailureView('serverIssue');
        }
    };

    private postDdcIframe = (
        url: string,
        jwt: string,
        cardinalOrigin: string,
        bin?: string
    ): Promise<string | undefined> => {
        return new Promise((resolve) => {
            const iframe = document.createElement('iframe');
            iframe.name = 'ddc-iframe';
            iframe.style.display = 'none';
            document.body.appendChild(iframe);

            const form = document.createElement('form');
            form.method = 'POST';
            form.action = url;
            form.target = 'ddc-iframe';

            const jwtInput = document.createElement('input');
            jwtInput.type = 'hidden';
            jwtInput.name = 'JWT';
            jwtInput.value = jwt;
            form.appendChild(jwtInput);

            if (bin !== undefined) {
                const binInput = document.createElement('input');
                binInput.type = 'hidden';
                binInput.name = 'Bin';
                binInput.value = bin;
                form.appendChild(binInput);
            }

            const timeout = setTimeout(() => {
                cleanup();
                resolve(undefined);
            }, 10000);

            const onMessage = (event: MessageEvent): void => {
                if (event.origin !== cardinalOrigin) return;
                try {
                    const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
                    if (data?.MessageType === 'profile.completed') {
                        cleanup();
                        resolve(data.Status === true && data.SessionId ? data.SessionId : undefined);
                    }
                } catch {
                    // ignore non-JSON messages
                }
            };

            const cleanup = (): void => {
                clearTimeout(timeout);
                window.removeEventListener('message', onMessage);
                document.body.removeChild(iframe);
                document.body.removeChild(form);
            };

            window.addEventListener('message', onMessage);
            document.body.appendChild(form);
            form.submit();
        });
    };

    private run3dsAuth = async (
        transactionId: number,
        amount: number,
        currency: string,
        tokenHref: string,
        paymentMethod: WorldpayCreatePaymentMethod,
        deviceData: z.infer<typeof DeviceDataSchema>,
        challenge: z.infer<typeof ThreeDsChallengeSchema>,
        backendOrigin: string,
        cardHolderName?: string
    ): Promise<void> => {
        const cardCvvSession =
            paymentMethod.flow === 'new' ? paymentMethod.cardCvvSession : paymentMethod.savedCardCvcSession;
        const authResponse = await this.trpcClient.client.worldpayRouter.authenticate3ds.mutate({
            amount,
            transactionId,
            currencyCode: currency,
            tokenHref,
            cardHolderName,
            deviceData,
            challenge,
        });

        if (authResponse.responseStatus === 'error') {
            console.error('worldpay-3ds-auth-error', authResponse.data);
            this.stepsState.redirectToFailureView('serverIssue');
            return;
        }

        const { outcome, authentication, challengeJwt, challengeReference, challengeUrl } = authResponse.response;
        const verifiedTokenHref = authResponse.response.tokenHref;

        if (
            outcome === 'authenticationFailed' ||
            outcome === 'signatureFailed' ||
            outcome === 'unavailable' ||
            outcome === 'authenticationOutage'
        ) {
            console.error('worldpay-3ds-auth-failed - outcome:', outcome);
            this.stepsState.redirectToFailureView('serverIssue');
            return;
        }

        if (
            outcome === 'authenticated' ||
            outcome === 'alreadyAuthenticated' ||
            outcome === 'bypassed' ||
            outcome === 'notEnrolled'
        ) {
            if (verifiedTokenHref == null || verifiedTokenHref === '') {
                console.error('worldpay-3ds-auth-missing-token');
                this.stepsState.redirectToFailureView('serverIssue');
                return;
            }

            await this.submitPayment({
                amount,
                currency,
                paymentMethod: {
                    type: 'Card',
                    flow: 'saved',
                    tokenHref: verifiedTokenHref,
                    savedCardCvcSession: cardCvvSession,
                },
                tokenHref: verifiedTokenHref,
                transactionId,
                ...(authentication ? { threeDsAuthentication: { threeDS: authentication } } : {}),
                allowThreeDs: false,
            });
            return;
        }

        if (outcome === 'challenged') {
            if (challengeUrl == null || challengeJwt == null || challengeReference == null) {
                throw new Error('worldpay-3ds-challenged-missing-fields');
            }

            const completedReference = await new Promise<string>((resolve, reject: (error: Error) => void) => {
                this.challengeResolve = resolve;
                this.challengeReject = reject;
                runInAction(() => {
                    this.challengeData = {
                        url: challengeUrl,
                        jwt: challengeJwt,
                        reference: challengeReference,
                        backendOrigin,
                    };
                });
            });

            runInAction(() => {
                this.challengeData = null;
            });

            const verifyResponse = await this.trpcClient.client.worldpayRouter.verify3ds.mutate({
                transactionId,
                challengeReference: completedReference,
            });

            if (verifyResponse.responseStatus === 'error') {
                console.error('worldpay-3ds-verify-error', verifyResponse.data);
                this.stepsState.redirectToFailureView('serverIssue');
                return;
            }

            const { outcome: verifyOutcome, authentication: verifyAuthentication } = verifyResponse.response;
            const verifiedTokenHref = authResponse.response.tokenHref;

            if (
                verifyOutcome === 'authenticationFailed' ||
                verifyOutcome === 'unavailable' ||
                verifyOutcome === 'signatureFailed'
            ) {
                console.error('worldpay-3ds-verify-failed - outcome:', verifyOutcome);
                this.stepsState.redirectToFailureView('serverIssue');
                return;
            }

            if (verifiedTokenHref == null || verifiedTokenHref === '') {
                console.error('worldpay-3ds-verify-missing-token');
                this.stepsState.redirectToFailureView('serverIssue');
                return;
            }

            await this.submitPayment({
                amount,
                currency,
                paymentMethod: {
                    type: 'Card',
                    flow: 'saved',
                    tokenHref: verifiedTokenHref,
                    savedCardCvcSession: cardCvvSession,
                },
                tokenHref: verifiedTokenHref,
                transactionId,
                ...(verifyAuthentication ? { threeDsAuthentication: { threeDS: verifyAuthentication } } : {}),
                allowThreeDs: false,
            });
            return;
        }

        console.error('worldpay-3ds-auth-failed - unknown outcome:', outcome);
        this.stepsState.redirectToFailureView('serverIssue');
    };

    public handleChallengeComplete = (challengeReference: string): void => {
        const resolve = this.challengeResolve;
        this.challengeResolve = null;
        this.challengeReject = null;

        this.challengeData = null;

        if (resolve !== null) {
            resolve(challengeReference);
        }
    };

    public handleChallengeError = (): void => {
        const reject = this.challengeReject;
        this.challengeResolve = null;
        this.challengeReject = null;

        this.challengeData = null;

        if (reject !== null) {
            reject(new Error('worldpay-3ds-challenge-error'));
        }
    };

    private handlePaymentSuccess = (_amount: number, transactionId: number): void => {
        if (!this.addedDepositTag) {
            this.addedDepositTag = true;
            this.googleTagManager.addDepositTag(this.account.account);
        }

        if (this.isSignup) {
            this.googleTagManager.depositedMoney(parseFloat(this.amount), true);
            this.googleTagManager.gtmSignUpStepFour(parseFloat(this.amount));
        } else {
            this.googleTagManager.depositedMoney(parseFloat(this.amount), false);
        }

        this.stepsState.redirectToFinalView(transactionId, 'worldpay');
    };
}
